// ============================================================
// narrativeDoc - one combat-code narrative entry, as the document the
// shared log stores.
//
// Pure. Split out of useNarrativeSync, which queues and sends these.
// The strings are asserted verbatim (useNarrativeSync.format.test.js):
// a character out of place here is a change every player sees.
// ============================================================

import type { NarrativeEntryDoc } from '../types';

/** Loose input shape accepted from combat code (formatted into NarrativeEntryDoc). */
export interface NarrativeInput {
  type?: string;
  text?: string;
  targetName?: string;
  slotName?: string;
  fireMode?: string;
  result?: Record<string, any>;
  isHorde?: boolean;
  magDmg?: number;
  magAfter?: number;
  magMax?: number;
  totalDamage?: number;
  woundsAfter?: number;
  woundsMax?: number;
  attackGroupId?: string;
  volleyId?: string | null;
  volleySize?: number | null;
  /** Who rolled, when it is not the writer (v9.5.3). */
  by?: string;
  /** The lane its author files it under (v9.7.0, engine/log/lanes). */
  lane?: string;
  structured?: unknown;
  [key: string]: unknown;
}

type AttackResult = Record<string, any>;

/**
 * THE SHARED LOG IS AN ALLOW-LIST: a field not named here never leaves
 * this machine. `volleyId` was declared in the schema and set on the
 * entry, and nothing changed on screen until it was named (v8.156.0);
 * the volley header names the weapon, so `slotName` travels too, or every
 * block read "Area attack". Copied when truthy, in this order.
 */
// `by` (v9.5.3): who ROLLED, when it is not the writer - a called test the player rolled on their own
// screen is written by the GM's and credited to the player. `lane` (v9.7.0): what kind of line its
// AUTHOR says it is (engine/log/lanes) - the story's lines say `story`; without it a line is its type.
// `beat` (plan map-display, D; engine/attackBeat): who struck, melee, landed/miss/jam - the display's
// beat on every OTHER screen reads it, and a miss says it was a miss nowhere else.
const ALLOW_LISTED = ['attackGroupId', 'volleyId', 'volleySize', 'slotName', 'by', 'lane', 'beat'] as const;

const noteLines = (r: AttackResult): string[] => (r.specialNotes || []).map((n: string) => '  ' + n);

const overheat = (r: AttackResult, prefix: string): string => (r.overheatDmg ? `${prefix}${r.overheatDmg} E!` : '');

function jamText(entry: NarrativeInput, r: AttackResult): string {
  return `JAM! ${entry.slotName || '?'} jams (rolled ${r.attackRoll}).` + overheat(r, ' Overheats: ');
}

function missText(entry: NarrativeInput, r: AttackResult): string {
  const lines = [
    `MISS ${entry.slotName || '?'} (${entry.fireMode || '?'}). Rolled ${r.attackRoll} vs ${r.modifiedSkill}. ${r.dof || 0} DoF.`,
  ];
  if (r.overheatDmg) lines.push(overheat(r, '  Overheats: '));
  return [...lines, ...noteLines(r)].join('\n');
}

/** A dodge or parry, if the result carries one. */
function defenceLine(label: string, verb: string, d: AttackResult | undefined): string[] {
  if (!d) return [];
  return [`  ${label}: ${d.roll} vs ${d.skill}` + (d.success ? ` - ${verb} ${d.avoided}` : ' - failed!')];
}

const forceFieldText = (h: AttackResult): string =>
  'FORCE FIELD blocked' + (h.ffOverloaded ? ' [OVERLOADED - field deactivated!]' : '');

const damageSumText = (h: AttackResult): string =>
  `${h.rawDamage || 0} raw - ${h.effectiveAP || 0} AP - ${h.tb || 0} TB = ${h.effectiveDamage || 0}`;

/** What the damage came to: absorbed, a critical (clipped to 80 characters), lethal. */
function damageSuffix(h: AttackResult): string {
  let s = h.effectiveDamage === 0 ? ' (absorbed)' : '';
  if (h.crit) s += ` | CRIT(Sev ${h.critSev}): ` + (h.crit.text || '').substring(0, 80);
  if (h.crit?.isDead) s += ' [LETHAL]';
  return s;
}

/** One hit after the defences: a force field's block, or the damage sum with its critical. */
function hitLine(h: AttackResult): string {
  const what = h.ffBlocked ? forceFieldText(h) : damageSumText(h) + damageSuffix(h);
  return `  ${h.location?.name || '?'}: ${what}`;
}

/** The closing line: what a horde lost, or the wounds dealt - or nothing when neither is positive. */
function damageLine(entry: NarrativeInput): string[] {
  if (entry.isHorde && entry.magDmg && entry.magDmg > 0) {
    const after = (entry.magAfter ?? 0) <= 0 ? 'HORDE DESTROYED!' : `Mag: ${entry.magAfter}/${entry.magMax}`;
    return [`  Mag dmg: ${entry.magDmg}. ${after}`];
  }
  if (!(entry.totalDamage && entry.totalDamage > 0)) return [];
  const woundsAfter = entry.woundsAfter ?? 0;
  const after =
    woundsAfter < 0 ? `CRITICAL! Sev ${Math.abs(woundsAfter)}` : `Remaining: ${entry.woundsAfter}/${entry.woundsMax}`;
  return [`  Total: ${entry.totalDamage} wounds. ${after}`];
}

function hitText(entry: NarrativeInput, r: AttackResult): string {
  return [
    `HIT! ${entry.slotName || '?'} (${entry.fireMode || '?'}). Rolled ${r.attackRoll} vs ${r.modifiedSkill}. ${r.dos} DoS = ${r.totalHits} hit(s).`,
    ...noteLines(r),
    ...defenceLine('Dodge', 'avoided', r.dodgeResult),
    ...defenceLine('Parry', 'deflected', r.parryResult),
    ...(r.hitsAfterDefence || []).map(hitLine),
    ...damageLine(entry),
  ].join('\n');
}

function attackResultText(entry: NarrativeInput): string {
  const r = entry.result || {};
  if (r.jam) return jamText(entry, r);
  return r.success ? hitText(entry, r) : missText(entry, r);
}

/** Serialized for the store, or left out when it cannot be. */
function copyStructured(entry: NarrativeInput, doc: NarrativeEntryDoc): void {
  if (!entry.structured) return;
  try {
    doc.structured = JSON.stringify(entry.structured);
  } catch {
    /* skip if not serializable */
  }
}

/**
 * The shared-log document for one entry.
 *
 * `info` and any unknown type carry their text; `attack-result` is
 * rendered from its result. An entry that renders to nothing reads
 * `[type]`. Throws only if a result is malformed beyond what the
 * renderers tolerate - the caller logs and drops the entry.
 */
export function narrativeDocFrom(entry: NarrativeInput, timestamp: number, userName: string): NarrativeEntryDoc {
  const doc: NarrativeEntryDoc = { timestamp, userName };
  if (entry.targetName) doc.targetName = entry.targetName;
  const isAttack = entry.type === 'attack-result';
  doc.type = isAttack ? 'attack-result' : entry.type || 'info';
  doc.text = (isAttack ? attackResultText(entry) : entry.text) || `[${entry.type || 'unknown'}]`;
  for (const key of ALLOW_LISTED) if (entry[key]) (doc as Record<string, unknown>)[key] = entry[key];
  copyStructured(entry, doc);
  return doc;
}
